import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import BackButton from "@/components/BackButton";
import ClosedFeature from "@/components/ClosedFeature";
import { Check, ArrowRight, AlertCircle, Waves, Calendar, Clock, Hotel } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const DAY_COLORS = [
  "#00F0FF", "#2563EB", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#EF4444"
];

// Time-based restrictions have been replaced by the global registration toggle

export default function EventRegistration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [existing, setExisting] = useState({ events: [], relay_events: [] });
  const [selected, setSelected] = useState([]);
  const [entryTimes, setEntryTimes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeDay, setActiveDay] = useState(0);
  const [hasPaidAccommodation, setHasPaidAccommodation] = useState(false);

  const ENTRY_TIME_RE = /^\d{1,2}:[0-5]\d\.\d{2}$/;

  // Lets athletes just type digits (e.g. "12345") instead of the ":" and "."
  // themselves — the separators are inserted automatically as they type.
  const formatEntryTimeDigits = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}.${digits.slice(4)}`;
  };

  const isNonAthlete = user?.participant_type === "Coach" || user?.participant_type === "Manager";

  const [unpaidReg, setUnpaidReg] = useState(null);

  const loadData = () => {
    Promise.all([
      api.get("/meta/events"),
      api.get("/schedule"),
      api.get("/dashboard/summary").catch(() => ({ data: null })),
    ]).then(([metaRes, schedRes, dashRes]) => {
      setMeta(metaRes.data);
      setSchedule(schedRes.data);
      const regs = dashRes.data?.registrations || [];
      const unpaid = regs.find(r => r.payment_status !== "paid") || null;
      const targetReg = unpaid || regs[0] || null;
      setUnpaidReg(unpaid);
      setHasPaidAccommodation(!!dashRes.data?.accommodations?.some(a => a.payment_status === "paid"));
      if (targetReg) {
        const allEvents = [...new Set(regs.flatMap(r => r.events || []))];
        const allRelays = [...new Set(regs.flatMap(r => r.relay_events || []))];
        setExisting({
          registration_id: targetReg.registration_id,
          events: allEvents,
          relay_events: allRelays,
          isSupplementary: !unpaid && regs.length > 0,
        });
      } else {
        setExisting({ events: [], relay_events: [] });
      }
    });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removingRef = React.useRef(false);
  const removeEvent = async (e) => {
    if (removingRef.current) return;
    removingRef.current = true;
    try {
      await api.patch(`/registrations/${unpaidReg.registration_id}/remove-events`, { events: [e] });
      toast.success(`Removed ${e} from your registration`);
      loadData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      removingRef.current = false;
    }
  };

  const maxIndividual = meta?.max_events || 5;
  const remaining = maxIndividual - existing.events.length;

  const toggle = (e) => {
    if (existing.events.includes(e)) return;
    setSelected((p) => {
      if (p.includes(e)) return p.filter((x) => x !== e);
      if (p.length >= remaining) {
        toast.error(`You can add ${remaining} more event${remaining !== 1 ? "s" : ""} (${maxIndividual} max)`);
        return p;
      }
      return [...p, e];
    });
    setEntryTimes((p) => {
      if (!(e in p)) return p;
      const next = { ...p };
      delete next[e];
      return next;
    });
  };

  const setEntryTime = (event, value) => {
    setEntryTimes((p) => ({ ...p, [event]: formatEntryTimeDigits(value) }));
  };

  const submit = async () => {
    if (!isNonAthlete && selected.length === 0) {
      toast.error("Select at least one event");
      return;
    }
    const missingOrInvalid = selected.filter((e) => !ENTRY_TIME_RE.test(entryTimes[e] || ""));
    if (missingOrInvalid.length > 0) {
      toast.error(`Enter a valid entry time (MM:SS.mm) for: ${missingOrInvalid.join(", ")}`);
      return;
    }
    const entry_times = Object.fromEntries(selected.map((e) => [e, entryTimes[e]]));
    setSubmitting(true);
    try {
      let data;
      if (existing.registration_id) {
        ({ data } = await api.patch(
          `/registrations/${existing.registration_id}/add-events`,
          { events: selected, relay_events: [], entry_times }
        ));
        toast.success("Events added to your registration");
      } else {
        ({ data } = await api.post("/registrations", {
          events: selected,
          relay_events: [],
          entry_times,
        }));
        toast.success(`Registration created: ${data.registration_id}`);
      }
      const reg_id = data?.registration_id || existing.registration_id;
      if (!hasPaidAccommodation) {
        if (user?.nationality !== "India") {
          toast.success("Events saved! Please complete your accommodation selection.");
          navigate(`/accommodation-choice?cart_reg_id=${reg_id}`);
        } else {
          toast.success("Events saved! Accommodation is optional — book a hotel or skip straight to payment.");
          navigate(`/accommodation?cart_reg_id=${reg_id}`);
        }
      } else {
        toast.success("Events selected! Redirecting to payment...");
        navigate(`/payment/${reg_id}`);
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const isIndian = user?.nationality === "India";
  const currencySymbol = isIndian ? "₹" : "$";
  const currencyCode = isIndian ? "INR" : "USD";
  const baseFee = meta ? (isIndian ? meta.base_fee_inr : meta.base_fee_usd) : 0;
  const perEventFee = meta ? (isIndian ? meta.per_event_fee_inr : meta.per_event_fee_usd) : 0;

  const isSupplementary = !!existing.isSupplementary;
  // True whenever the athlete already has ANY registration on file — paid or
  // still pending — since the base registration fee is already baked into
  // that registration's total and shouldn't be presented as a fresh charge.
  const hasExistingRegistration = existing.events.length > 0;
  const indFee = selected.length * perEventFee;
  const total = hasExistingRegistration ? indFee : baseFee + indFee;

  // Build day-wise structure from schedule, only individual events
  const days = schedule?.days?.map(day => {
    const individualEvents = [];
    day.sessions?.forEach(session => {
      session.events?.forEach(evt => {
        if (evt.category === "individual") {
          individualEvents.push(evt.name);
        }
      });
    });
    return {
      day: day.day,
      label: day.label,
      date: day.date,
      warmup: day.warmup,
      time: day.sessions?.[0]?.time || "",
      events: individualEvents,
    };
  }) || [];

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  // Indian athletes when events are globally closed. International event
  // registration is closed entirely (see the ClosedFeature check below).
  const needsHotelFirst = meta && meta.accommodation_open && !meta.event_registration_open && user?.nationality === "India";

  if (!meta || !schedule) {
    return (
      <div className="min-h-screen bg-[#0B1429]">
        <Navbar />
        <div className="pt-28 pb-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 space-y-4">
          <div className="h-8 w-32 bg-white/[0.03] animate-pulse rounded mb-6" />
          <div className="h-12 w-64 bg-white/[0.03] animate-pulse rounded mb-8" />
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="h-10 w-full bg-white/[0.03] animate-pulse rounded-xl" />
              <div className="h-64 w-full bg-white/[0.03] animate-pulse rounded-2xl" />
            </div>
            <div className="h-96 w-full bg-white/[0.03] animate-pulse rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!meta.registration_open && user?.role !== "admin") {
    return (
      <ClosedFeature
        featureName="Event Registration"
        icon={Calendar}
        subtitle="REGISTRATION CLOSED"
        description="Event registration is currently closed. Please check back later."
      />
    );
  }

  if (!meta.event_registration_open && user?.role !== "admin" && user?.nationality === "India") {
    const sfiStatus = user?.sfi_results_status;
    return (
      <ClosedFeature
        featureName="Event Registration"
        icon={Calendar}
        subtitle={sfiStatus === "pending" ? "SFI RESULTS UNDER REVIEW" : sfiStatus === "rejected" ? "SFI RESULTS NOT APPROVED" : "SFI VERIFICATION REQUIRED"}
        description={
          sfiStatus === "pending"
            ? "Your SFI National Masters Results submission is under review. You'll be notified once it's verified."
            : sfiStatus === "rejected"
              ? "Your SFI National Masters Results submission was not approved. Re-upload it from your dashboard."
              : "Upload your SFI National Masters Results document from your dashboard to unlock event registration."
        }
      />
    );
  }

  if (!meta.event_registration_open && user?.role !== "admin" && user?.nationality !== "India") {
    return (
      <ClosedFeature
        featureName="Event Registration"
        icon={Calendar}
        subtitle="CLOSED FOR INTERNATIONAL ATHLETES"
        description="Event registration is currently open for Indian athletes only. International registration is closed."
        badgeLabel=""
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="eyebrow mb-3">{isNonAthlete ? "Registration" : (isSupplementary ? "Add Events" : "Event Selection")}</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl mb-2">
          {isNonAthlete ? <>Register as <span className="text-[#00F0FF]">{user?.participant_type}</span></> : (isSupplementary ? <>Add more <span className="text-[#00F0FF]">events</span></> : <>Choose your <span className="text-[#00F0FF]">events</span></>)}
        </h1>
        <p className="text-[#94A3B8] mb-2">
          {isNonAthlete ? `${user?.participant_type}s pay a flat registration fee and do not register for swimming events.` : `Max ${maxIndividual} individual events. Your Masters Category: `}
          {!isNonAthlete && <span className="text-[#00F0FF] font-semibold">{user?.masters_category}</span>}
        </p>

        {isSupplementary && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-[#10B981]/[0.08] border border-[#10B981]/20 mb-8">
            <Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" strokeWidth={2} />
            <div>
              <p className="text-sm font-semibold text-[#10B981]">Registration fee already paid</p>
              <p className="text-xs text-[#94A3B8] mt-0.5">
                You have {existing.events.length} event{existing.events.length !== 1 ? "s" : ""} registered.
                {remaining > 0 ? ` You can add ${remaining} more — only per-event fees apply.` : " You have reached the 5-event maximum."}
              </p>
            </div>
          </div>
        )}
        {!isSupplementary && existing.events.length > 0 && (
          <p className="text-sm text-[#FBBF24] mb-8">
            You have {existing.events.length} event{existing.events.length !== 1 ? "s" : ""} in your registration.
            {remaining > 0 ? ` You can add ${remaining} more.` : " You have reached the maximum of 5 events."}
          </p>
        )}
        {existing.events.length === 0 && <div className="mb-8" />}

        <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FBBF24]/[0.08] border border-[#FBBF24]/20 mb-6">
          <AlertCircle className="w-4 h-4 text-[#FBBF24] mt-0.5 shrink-0" />
          <p className="text-xs text-[#CBD5E1]">
            <strong className="text-[#FBBF24]">Relay events are registered on-spot at the venue</strong>, not through this form.
            Relay fee: <strong className="text-white">{isIndian ? `₹${meta?.relay_fee_inr ?? 3000}` : "$40"}</strong> per team entry{isIndian ? " for Indian athletes" : ""}.
          </p>
        </div>

        {user?.masters_category === "Under 25 (Not Eligible)" && (
          <div className="card-dark p-5 mb-6 border-[#FF3B30]/40 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#FF3B30]" />
            <div className="text-sm">You must be 25 or older as of Dec 31, 2026 to participate in the AMSC India Masters championship.</div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {isNonAthlete ? (
            <div className="lg:col-span-2 space-y-0 min-w-0">
              <div className="card-dark p-6 border-[#00F0FF]/30 bg-[#00F0FF]/[0.02]">
                <h2 className="text-xl font-bold mb-4">Registration Details</h2>
                <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
                  You are registering as a <strong>{user?.participant_type}</strong>. 
                  As a non-competing participant, you are only required to pay the base registration fee of {currencySymbol}{baseFee}.
                  Once registered, you can proceed to book accommodation for the event.
                </p>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[#142040] border border-[#2A3A5F]">
                  <Check className="w-5 h-5 text-[#00F0FF]" />
                  <div>
                    <div className="text-sm font-semibold">Registration Fee</div>
                    <div className="text-xs text-[#94A3B8]">{currencySymbol}{baseFee} (No event selection required)</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="lg:col-span-2 space-y-0 min-w-0">

              {/* Day tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/10">
                {days.map((day, i) => (
                  <button
                    key={day.day}
                    onClick={() => setActiveDay(i)}
                    className={[
                      "flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold whitespace-nowrap transition-all shrink-0",
                      activeDay === i
                        ? "border-[#00F0FF] bg-[#00F0FF]/10 text-white shadow-[0_0_15px_rgba(0,240,255,0.15)]"
                        : "border-[#2A3A5F] bg-[#142040] text-[#94A3B8] hover:border-[#00F0FF]/30 hover:text-white"
                    ].join(" ")}
                  >
                    <Calendar className="w-3.5 h-3.5" style={{ color: DAY_COLORS[i % DAY_COLORS.length] }} />
                    <span>{day.label}</span>
                    <span className="text-[10px] opacity-60">{formatDate(day.date)}</span>
                  </button>
                ))}
              </div>

              {/* Day content */}
              <AnimatePresence mode="wait">
                {days[activeDay] && (
                  <motion.div
                    key={activeDay}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.25 }}
                  >
                    {/* Day header card */}
                    <div
                      className="rounded-xl p-4 mb-5 border"
                      style={{
                        borderColor: `${DAY_COLORS[activeDay % DAY_COLORS.length]}33`,
                        background: `linear-gradient(135deg, ${DAY_COLORS[activeDay % DAY_COLORS.length]}08, transparent)`,
                      }}
                    >
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" style={{ color: DAY_COLORS[activeDay % DAY_COLORS.length] }} />
                          <span className="text-white font-semibold">{formatDate(days[activeDay].date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#94A3B8]">
                          <Clock className="w-4 h-4" />
                          <span>Warm-up: {days[activeDay].warmup}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#94A3B8]">
                          <Waves className="w-4 h-4" style={{ color: DAY_COLORS[activeDay % DAY_COLORS.length] }} />
                          <span>Racing from {days[activeDay].time}</span>
                        </div>
                      </div>
                    </div>

                    {/* Events for this day */}
                    <div className="grid sm:grid-cols-2 gap-3">
                      {days[activeDay].events.map((e) => {
                        const isRegistered = existing.events.includes(e);
                        const isRemovable = isRegistered && !!unpaidReg?.events?.includes(e);
                        const isSel = selected.includes(e);
                        const isDisabled = isRegistered || (remaining === 0 && !isSel);

                        const label = (
                          <div>
                            <div className="font-semibold text-sm">{e}</div>
                            <div className="text-[10px] text-[#94A3B8] mt-0.5">{days[activeDay].label} · {days[activeDay].time}</div>
                          </div>
                        );

                        if (isRegistered) {
                          return (
                            <div
                              key={e}
                              data-testid={`event-${e.replace(/\s/g, '-')}`}
                              className="text-left p-4 rounded-lg border transition-all flex items-center justify-between group border-[#10B981]/30 bg-[#10B981]/[0.06]"
                            >
                              {label}
                              {isRemovable ? (
                                <button
                                  onClick={() => removeEvent(e)}
                                  data-testid={`remove-event-${e.replace(/\s/g, '-')}`}
                                  title="Remove from your unpaid registration"
                                  className="text-[10px] font-bold text-[#FF3B30] uppercase tracking-wider shrink-0 ml-2 hover:text-[#FF6B6B] transition-colors py-3 -my-3 touch-manipulation"
                                >
                                  Remove ✕
                                </button>
                              ) : (
                                <span className="text-[10px] font-bold text-[#10B981] uppercase tracking-wider shrink-0 ml-2">Registered</span>
                              )}
                            </div>
                          );
                        }

                        return (
                          <button
                            key={e}
                            data-testid={`event-${e.replace(/\s/g, '-')}`}
                            onClick={() => toggle(e)}
                            disabled={isDisabled}
                            className={[
                              "text-left p-4 rounded-lg border transition-all flex items-center justify-between group",
                              isSel
                                ? "border-[#00F0FF] bg-[#00F0FF]/5 shadow-[0_0_12px_rgba(0,240,255,0.1)]"
                                : isDisabled
                                  ? "border-[#2A3A5F] bg-[#142040] opacity-40 cursor-not-allowed"
                                  : "border-[#2A3A5F] bg-[#142040] hover:border-[#00F0FF]/40 cursor-pointer hover:bg-[#142040]/80",
                            ].join(" ")}
                          >
                            {label}
                            {isSel
                              ? <Check className="w-5 h-5 text-[#00F0FF] shrink-0" />
                              : <div className="w-5 h-5 rounded border border-[#2A3A5F] group-hover:border-[#00F0FF]/50 shrink-0 transition-colors" />}
                          </button>
                        );
                      })}
                    </div>

                    {days[activeDay].events.length === 0 && (
                      <div className="text-center py-10 text-[#94A3B8] text-sm">
                        No individual events scheduled for this day.
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Summary card */}
          <div className="card-dark p-6 h-fit sticky top-24" data-testid="cart-card">
            <div className="eyebrow mb-3">Your Selection</div>
            {!isNonAthlete && (
              <div className="flex items-end gap-6 mb-2">
                <div>
                  <div className="text-3xl font-display font-black text-[#00F0FF]">
                    {selected.length}
                    <span className="text-[#94A3B8] text-base"> / {remaining}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">
                    {isSupplementary ? `of ${remaining} remaining` : "Individual Events"}
                  </div>
                </div>
              </div>
            )}
            <div className="divider-line my-4" />

            <div className="space-y-1.5 mb-4 max-h-52 overflow-auto scroll-fade">
              {!isNonAthlete && selected.length === 0 && (
                <div className="text-xs text-[#94A3B8]">No events selected yet — browse days above</div>
              )}
              {isNonAthlete && (
                <div className="text-xs text-[#94A3B8]">No events required for {user?.participant_type === "Coach" ? "Coaches" : `${user?.participant_type}s`}.</div>
              )}
              {selected.map((s) => {
                // Find which day this event is on
                const dayInfo = days.find(d => d.events.includes(s));
                const timeVal = entryTimes[s] || "";
                const timeValid = ENTRY_TIME_RE.test(timeVal);
                return (
                  <div key={s} className="text-sm bg-[#0B1429] border border-[#2A3A5F] rounded-lg px-3 py-2.5">
                    <div className="flex justify-between items-center">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{s}</div>
                        {dayInfo && <div className="text-[10px] text-[#94A3B8]">{dayInfo.label} · {formatDate(dayInfo.date)}</div>}
                      </div>
                      <button onClick={() => toggle(s)} className="text-[#FF3B30] text-xs ml-2 hover:text-[#FF6B6B] transition-colors shrink-0 py-3 px-2 -my-3 -mr-2 touch-manipulation">✕</button>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={timeVal}
                      onChange={(ev) => setEntryTime(s, ev.target.value)}
                      placeholder="Entry time — just type digits (MM:SS.mm)"
                      data-testid={`entry-time-${s.replace(/\s/g, '-')}`}
                      className="input-dark mt-2 w-full text-xs py-1.5"
                      style={timeVal && !timeValid ? { borderColor: "#FF3B30" } : undefined}
                    />
                  </div>
                );
              })}
            </div>

            <div className="bg-[#0B1429] rounded-lg p-4 mb-4 space-y-2 text-sm">
              {isSupplementary ? (
                <div className="flex justify-between text-[#10B981]">
                  <span className="flex items-center gap-1.5"><Check className="w-3 h-3" strokeWidth={2.5} /> Registration fee</span>
                  <span>Already paid</span>
                </div>
              ) : hasExistingRegistration ? (
                <div className="flex justify-between text-[#94A3B8]">
                  <span className="flex items-center gap-1.5"><Check className="w-3 h-3" strokeWidth={2.5} /> Registration fee</span>
                  <span>Included</span>
                </div>
              ) : (
                <div className="flex justify-between text-[#94A3B8]"><span>Registration</span><span>{currencySymbol}{baseFee}</span></div>
              )}
              {!isNonAthlete && (
                <div className="flex justify-between text-[#94A3B8]">
                  <span>{selected.length} × {currencySymbol}{perEventFee} / event</span>
                  <span>{currencySymbol}{indFee}</span>
                </div>
              )}
              <div className="divider-line" />
              <div className="flex justify-between font-display font-bold text-lg">
                <span>{hasExistingRegistration ? "Add-on total" : "Total due"}</span>
                <span className="text-[#00F0FF]">{currencySymbol}{total} {currencyCode}</span>
              </div>
            </div>
            <button
              data-testid="confirm-events-btn"
              disabled={submitting || (!isNonAthlete && selected.length === 0)}
              onClick={submit}
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Confirm & Proceed"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
